import { type ChangeEventHandler, type DragEventHandler, useRef, useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

import { profileApi, profileQueryKeys, type Profile } from '@entities/profile';
import { showToast } from '@features/ToastManager';

import { PROFILE_TOAST_SCOPE } from './toast';
import {
    AVATAR_MAX_DIMENSION_PX,
    AVATAR_MAX_SIZE_MB,
    canvasToAvatarFile,
    getAvatarFileValidationError,
    getImageDimensions,
    readFileAsDataUrl,
} from '../lib/avatarFile';

import type { IDataResponseError } from '@shared/model/types/dataResponse.inteface';
import type { ReactCropperElement } from 'react-cropper';

export const useAvatarUpload = () => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const cropperRef = useRef<ReactCropperElement>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const dragCounterRef = useRef(0);
    const [imageSrc, setImageSrc] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isDragActive, setIsDragActive] = useState(false);

    const mutation = useMutation({
        mutationFn: profileApi.updateAvatar,
        onSuccess: (avatarUrl) => {
            queryClient.setQueryData<Profile>(profileQueryKeys.current(), (current) =>
                current ? { ...current, user: { ...current.user, avatarUrl } } : current
            );
            document.querySelectorAll<HTMLImageElement>('.user-image').forEach((image) => {
                image.src = avatarUrl;
            });
            setImageSrc(null);
            setError(null);
            showToast(t('profile.page.avatar.success'), { scope: PROFILE_TOAST_SCOPE });
        },
        onError: (mutationError) => {
            const responseError = isAxiosError<IDataResponseError>(mutationError)
                ? mutationError.response?.data
                : undefined;
            setError(responseError?.message ?? t('profile.page.avatar.errors.upload'));
        },
    });

    const handleFile = async (file?: File) => {
        setError(null);
        if (!file) {
            return;
        }

        const validationError = getAvatarFileValidationError(file);
        if (validationError) {
            setError(
                validationError === 'fileSize'
                    ? t('profile.page.avatar.errors.fileSize', { size: AVATAR_MAX_SIZE_MB })
                    : t('profile.page.avatar.errors.fileType')
            );
            return;
        }

        try {
            const src = await readFileAsDataUrl(file);
            const { width, height } = await getImageDimensions(src);
            if (width > AVATAR_MAX_DIMENSION_PX || height > AVATAR_MAX_DIMENSION_PX) {
                setError(
                    t('profile.page.avatar.errors.dimensions', { size: AVATAR_MAX_DIMENSION_PX })
                );
                return;
            }
            setImageSrc(src);
        } catch {
            setError(t('profile.page.avatar.errors.read'));
        }
    };

    const handleInputChange: ChangeEventHandler<HTMLInputElement> = (event) => {
        void handleFile(event.target.files?.[0]);
        event.target.value = '';
    };
    const handleDragEnter: DragEventHandler<HTMLDivElement> = (event) => {
        event.preventDefault();
        event.stopPropagation();
        dragCounterRef.current += 1;
        setIsDragActive(event.dataTransfer.items.length > 0);
    };
    const handleDragOver: DragEventHandler<HTMLDivElement> = (event) => {
        event.preventDefault();
        event.stopPropagation();
        event.dataTransfer.dropEffect = 'copy';
        setIsDragActive(true);
    };
    const handleDragLeave: DragEventHandler<HTMLDivElement> = (event) => {
        event.preventDefault();
        event.stopPropagation();
        dragCounterRef.current = Math.max(dragCounterRef.current - 1, 0);
        if (dragCounterRef.current === 0) {
            setIsDragActive(false);
        }
    };
    const handleDrop: DragEventHandler<HTMLDivElement> = (event) => {
        event.preventDefault();
        event.stopPropagation();
        dragCounterRef.current = 0;
        setIsDragActive(false);
        void handleFile(event.dataTransfer.files[0]);
    };

    const submit = async () => {
        const cropper = cropperRef.current?.cropper;
        if (!cropper) {
            return;
        }

        try {
            const file = await canvasToAvatarFile(
                cropper.getCroppedCanvas({
                    maxWidth: AVATAR_MAX_DIMENSION_PX,
                    maxHeight: AVATAR_MAX_DIMENSION_PX,
                })
            );
            if (getAvatarFileValidationError(file) === 'fileSize') {
                setError(t('profile.page.avatar.errors.fileSize', { size: AVATAR_MAX_SIZE_MB }));
                return;
            }
            mutation.mutate(file);
        } catch {
            setError(t('profile.page.avatar.errors.upload'));
        }
    };

    return {
        t,
        cropperRef,
        fileInputRef,
        imageSrc,
        error,
        isDragActive,
        isUploading: mutation.isPending,
        openFileDialog: () => fileInputRef.current?.click(),
        resetCrop: () => {
            setImageSrc(null);
            setError(null);
        },
        rotateLeft: () => cropperRef.current?.cropper.rotate(-90),
        rotateRight: () => cropperRef.current?.cropper.rotate(90),
        submit,
        handleInputChange,
        handleDragEnter,
        handleDragOver,
        handleDragLeave,
        handleDrop,
    };
};
